import { useState, useEffect, useRef } from 'react';
import { supabaseProjectManager } from '@/lib/supabaseProjectManager';
import { Camera, Video, Share2, Play } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { storageImage, storageSrcSet } from "@/lib/imageUrl";

interface LazyCoverImageProps {
  projectId: string;
  category: "photography" | "videography" | "social-media";
  title: string;
  className?: string;
}

const categoryIcons = {
  photography: Camera,
  videography: Video,
  "social-media": Share2,
};

export const LazyCoverImage = ({ projectId, category, title, className = "" }: LazyCoverImageProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageUrl && !hasError) {
            loadCoverImage();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [projectId, imageUrl, hasError]);

  const loadCoverImage = async () => {
    try {
      setIsLoading(true);
      const data = await supabaseProjectManager.getCoverImage(projectId);
      
      if (!data) {
        setHasError(true);
        return;
      }

      let finalUrl = data.coverImage;

      // If cover is a video, try to find a non-video image
      if (finalUrl?.startsWith('video::')) {
        const nonVideoImage = data.images?.find((img: string) => !img.startsWith('video::'));
        if (nonVideoImage) {
          finalUrl = nonVideoImage;
        }
      }

      // Google Drive media is not shown on public pages (no request to Google before consent)
      if (finalUrl && (finalUrl.includes('drive.google.com') || finalUrl.includes('gdrive::'))) {
        finalUrl = undefined;
      }

      setImageUrl(finalUrl || null);
    } catch (error) {
      console.error('Error loading cover image:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const Icon = categoryIcons[category];

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div ref={imgRef} className={`relative ${className}`}>
        <Skeleton className="w-full h-full" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-12 h-12 text-muted-foreground/30 animate-pulse" />
        </div>
      </div>
    );
  }

  // Show error state or video placeholder
  if (hasError || !imageUrl || imageUrl.startsWith('video::')) {
    return (
      <div ref={imgRef} className={`relative bg-muted ${className}`}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="relative">
            <Icon className="w-12 h-12 text-muted-foreground/50" />
            {imageUrl?.startsWith('video::') && (
              <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1">
                <Play className="w-3 h-3 text-primary-foreground fill-current" />
              </div>
            )}
          </div>
          <span className="text-xs text-muted-foreground text-center px-4">
            {imageUrl?.startsWith('video::') ? 'Video Content' : 'Preview'}
          </span>
        </div>
      </div>
    );
  }

  // Show the loaded image
  return (
    <div ref={imgRef} className={`relative ${className}`}>
      <img
        src={storageImage(imageUrl, 800)}
        srcSet={storageSrcSet(imageUrl)}
        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
        alt={title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
        decoding="async"
        onError={() => setHasError(true)}
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
      />
    </div>
  );
};
